import { useReadQuery } from "@apollo/client";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { BarChart } from "components/Dashboard/BarChart";
import ChartLegend from "components/Dashboard/ChartLegend";
import { convertAbbreviationToStateName } from "encamp-shared/src/utils/states";
import { RegulatoryStatusOption } from "generated-graphql/graphql";
import { useMemo, useState } from "react";

import { ALL_GENERATOR_STATUS_OPTIONS } from "../../types";
import { useWasteOverviewState } from "../../WasteOverviewContext";
import { statusAbbreviatedLabels, getStatusColors } from "./constants";

type ChartDataItem = {
  state: string;
  fullStateName: string;
  [key: string]: number | string;
};

export function FacilitiesByLocationAndGeneratorStatusChart() {
  const theme = useTheme();
  const colors = getStatusColors(theme);

  const {
    overviewState: { filters },
    setOverviewState,
    facilitiesByLocationAndGeneratorStatusQuery,
  } = useWasteOverviewState();
  const { data } = useReadQuery(facilitiesByLocationAndGeneratorStatusQuery);

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  // Transform data for the bar chart (by state and generator status)
  const barChartData = useMemo(() => {
    // Use Map for O(n) lookup instead of O(n²) find operations
    const stateMap = new Map<string, ChartDataItem>();

    data.wasteFacilitiesByGeneratorStatusAndState.forEach((item) => {
      // Only include counts for selected statuses, or all if none selected
      if (filters.currentGeneratorStatuses && filters.currentGeneratorStatuses.length > 0) {
        if (!filters.currentGeneratorStatuses.includes(item.generatorStatus)) {
          return;
        }
      }

      const existingState = stateMap.get(item.state);
      if (existingState) {
        existingState[item.generatorStatus] = item.count;
      } else {
        // Initialize all generator statuses to 0, then set the current one
        const newState: ChartDataItem = {
          state: item.state,
          fullStateName: convertAbbreviationToStateName(item.state) || item.state,
        };

        // Initialize all generator statuses to 0
        ALL_GENERATOR_STATUS_OPTIONS.forEach((status) => {
          newState[status] = 0;
        });

        // Set the current generator status count
        newState[item.generatorStatus] = item.count;

        stateMap.set(item.state, newState);
      }
    });

    // Convert Map to array and sort by total count descending
    const result = Array.from(stateMap.values()).sort((a, b) => {
      const totalA = ALL_GENERATOR_STATUS_OPTIONS.reduce(
        (sum, status) => sum + (typeof a[status] === "number" ? a[status] : 0),
        0
      );
      const totalB = ALL_GENERATOR_STATUS_OPTIONS.reduce(
        (sum, status) => sum + (typeof b[status] === "number" ? b[status] : 0),
        0
      );
      return totalB - totalA;
    });

    return result;
  }, [data.wasteFacilitiesByGeneratorStatusAndState, filters.currentGeneratorStatuses]);

  // Paginate bar chart data
  const paginatedBarChartData = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    return barChartData.slice(startIndex, startIndex + itemsPerPage);
  }, [barChartData, currentPage]);

  const totalPages = Math.ceil(barChartData.length / itemsPerPage);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  const hasItems = useMemo(() => (barChartData?.length ?? 0) > 0, [barChartData]);

  const maxTotalCount = useMemo(() => {
    if (!barChartData[0]) return 0;

    return ALL_GENERATOR_STATUS_OPTIONS.reduce(
      (sum, status) => sum + ((barChartData[0][status] as number) || 0),
      0
    );
  }, [barChartData]);

  const legendItems = useMemo(
    () =>
      Object.entries(statusAbbreviatedLabels).map(([key, label]) => {
        const isSelected = filters.currentGeneratorStatuses?.includes(
          key as RegulatoryStatusOption
        );
        return {
          color: getStatusColors[key as keyof typeof getStatusColors],
          value: label,
          isSelected,
        };
      }),
    [filters.currentGeneratorStatuses]
  );

  return (
    <Box sx={{ position: "relative" }}>
      <ChartLegend
        sx={{ pl: 6 }}
        config={{ direction: "row", position: "above" }}
        items={legendItems}
        onItemClick={(index) => {
          const key = Object.entries(statusAbbreviatedLabels)[index][0];

          setOverviewState((state) => {
            const currentFilter = state.currentGeneratorStatuses || [];
            const isCurrentlyFilteredToThis =
              currentFilter.length === 1 && currentFilter[0] === key;

            if (isCurrentlyFilteredToThis) {
              state.currentGeneratorStatuses = [...ALL_GENERATOR_STATUS_OPTIONS];
            } else {
              state.currentGeneratorStatuses = [key as RegulatoryStatusOption];
            }
          });

          // reset pagination
          setCurrentPage(0);
        }}
      />

      <BarChart
        noData={!hasItems}
        noDataMessage="🏭 No facilities to show"
        dataset={
          hasItems
            ? paginatedBarChartData
            : [{ state: "", fullStateName: "No facilities to show", count: 0 }]
        }
        yAxis={[{ min: 0, max: maxTotalCount }]}
        xAxis={[{ scaleType: "band", dataKey: "state", categoryGapRatio: 0.5 }]}
        tooltipTitleProperty="fullStateName"
        series={[
          {
            type: "bar",
            dataKey: RegulatoryStatusOption.None,
            label: "None",
            color: colors.NONE,
            stack: "generatorStatus",
            valueFormatter: (value) => `${statusAbbreviatedLabels.NONE} (${value})`,
          },
          {
            type: "bar",
            dataKey: RegulatoryStatusOption.VerySmallQuantityGenerator,
            label: statusAbbreviatedLabels.VERY_SMALL_QUANTITY_GENERATOR,
            color: colors.VERY_SMALL_QUANTITY_GENERATOR,
            stack: "generatorStatus",
            valueFormatter: (value) =>
              `${statusAbbreviatedLabels.VERY_SMALL_QUANTITY_GENERATOR} (${value})`,
          },
          {
            type: "bar",
            dataKey: RegulatoryStatusOption.SmallQuantityGenerator,
            label: statusAbbreviatedLabels.SMALL_QUANTITY_GENERATOR,
            color: colors.SMALL_QUANTITY_GENERATOR,
            stack: "generatorStatus",
            valueFormatter: (value) =>
              `${statusAbbreviatedLabels.SMALL_QUANTITY_GENERATOR} (${value})`,
          },
          {
            type: "bar",
            dataKey: RegulatoryStatusOption.LargeQuantityGenerator,
            label: statusAbbreviatedLabels.LARGE_QUANTITY_GENERATOR,
            color: colors.LARGE_QUANTITY_GENERATOR,
            stack: "generatorStatus",
            valueFormatter: (value) =>
              `${statusAbbreviatedLabels.LARGE_QUANTITY_GENERATOR} (${value})`,
          },
        ]}
        height={300}
        margin={{ top: 20 }}
      />
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 1,
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ alignSelf: "center" }}>
            {currentPage * itemsPerPage + 1}-
            {Math.min((currentPage + 1) * itemsPerPage, barChartData.length)} of{" "}
            {barChartData.length}
          </Typography>
          <IconButton
            onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
            disabled={!hasPrevPage}
            size="small"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
            disabled={!hasNextPage}
            size="small"
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
